import { useContext, useState } from 'react';
import { TextField } from '@mui/material';
import { useFormik } from 'formik';
import { Settings } from '@graphql';
import { BorderColorOutlined } from '@mui/icons-material';
import { ConfirmPhoneVerificationModal, CustomButton, CustomDrawer } from '@components';
import { appContext, ReauthChallengeErrorCode } from '@utils';
import { globalSettingsStyles } from '../../utils';

interface IRiskToleranceDrawerProps {
  handleClose: () => void;
  open: boolean;
  settings: Settings;
}

export function RiskToleranceDrawer({ open, handleClose, settings }: IRiskToleranceDrawerProps) {
  const sx = globalSettingsStyles();
  const [showReauthChallenge, setShowReauthChallenge] = useState(false);
  const {
    settingsStore: { fetchSettings, updateSettings },
  } = useContext(appContext);

  const formik = useFormik({
    initialValues: settings,
    onSubmit: async (values) => {
      if (!settings) return;
      const result = await updateSettings(values);
      if (result === ReauthChallengeErrorCode.StepUpRequired) {
        setShowReauthChallenge(true);
        return;
      }
      if (result) {
        await fetchSettings();
        handleClose();
      }
    },
    enableReinitialize: true,
  });

  return (
    <>
    <CustomDrawer
      open={open}
      onClose={handleClose}
      headerLabel="Edit Risk Tolerance Levels"
      headerIcon={<BorderColorOutlined />}
    >
      <form style={sx.formWrapper} onSubmit={formik.handleSubmit}>
        <div style={sx.formContainer}>
          <TextField
            sx={sx.formItems}
            fullWidth
            label="Growth"
            name="riskTolerance.growth"
            onChange={formik.handleChange}
            value={formik.values.riskTolerance?.growth || ''}
            variant="outlined"
            type="number"
            required
            InputProps={{
              sx: sx.inputBorder,
            }}
          />
          <TextField
            sx={sx.formItems}
            fullWidth
            label="Moderate Growth"
            name="riskTolerance.moderateGrowth"
            onChange={formik.handleChange}
            value={formik.values.riskTolerance?.moderateGrowth || ''}
            variant="outlined"
            type="number"
            required
            InputProps={{
              sx: sx.inputBorder,
            }}
          />

          <TextField
            sx={sx.formItems}
            fullWidth
            label="Moderate"
            name="riskTolerance.moderate"
            onChange={formik.handleChange}
            value={formik.values.riskTolerance?.moderate || ''}
            variant="outlined"
            type="number"
            required
            InputProps={{
              sx: sx.inputBorder,
            }}
          />
          <TextField
            sx={sx.formItems}
            fullWidth
            label="Conservative Growth"
            name="riskTolerance.conservativeGrowth"
            onChange={formik.handleChange}
            value={formik.values.riskTolerance?.conservativeGrowth || ''}
            variant="outlined"
            type="number"
            required
            InputProps={{
              sx: sx.inputBorder,
            }}
          />

          <TextField
            sx={sx.formItems}
            fullWidth
            label="Conservative"
            name="riskTolerance.conservative"
            onChange={formik.handleChange}
            value={formik.values.riskTolerance?.conservative || ''}
            variant="outlined"
            type="number"
            required
            InputProps={{
              sx: sx.inputBorder,
            }}
          />
        </div>

        <div style={sx.button}>
          <CustomButton variant="default" type="submit" isLoading={formik.isSubmitting}>
            Save Changes
          </CustomButton>
        </div>
      </form>
    </CustomDrawer>
    <ConfirmPhoneVerificationModal isOpen={showReauthChallenge} onClose={() => setShowReauthChallenge(false)} callBackFunction={formik.submitForm} />
    </>
  );
}
